import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-terminal-log',
  templateUrl: './terminal-log.component.html',
  styleUrls: ['./terminal-log.component.scss']
})
export class TerminalLogComponent implements OnInit, OnDestroy {

  @Input() storeId: string = '';

  logs: any[] = [];
  isLoading: boolean = false;
  errorMessage: string = '';

  // Pagination
  currentPage: number = 1;
  totalPages: number = 1;
  totalItems: number = 0;
  pageSize: number = 20;

  // Filters
  dateFrom: string = '';
  dateTo: string = '';
  selectedOutcome: string = '';
  searchText: string = '';
  orderSaveFailedOnly: boolean = false;

  // Detail modal
  showLogDetails: boolean = false;
  selectedLog: any = null;
  loadingDetail: boolean = false;

  // Reconciliation
  reconcileMode: 'link' | 'manual' = 'manual';
  reconciliationNote: string = '';
  orderSearchQuery: string = '';
  orderSearchResults: any[] = [];
  orderSearchLoading: boolean = false;
  selectedOrderId: string = '';
  manualItems: any[] = [];
  loadingBasketPrefill: boolean = false;
  basketPrefillSource: string = '';
  submittingReconcile: boolean = false;

  // Auto-refresh
  autoRefreshEnabled: boolean = true;
  refreshIntervalSec: number = 30;
  private refreshSubscription?: Subscription;
  private countdownSub?: Subscription;
  private logsLoadSeq = 0;
  countdown: number = 0;

  Math = Math;

  outcomeOptions = [
    { value: '', labelKey: 'TERMINAL_LOG.FILTER_ALL_OUTCOMES' },
    { value: 'approved', labelKey: 'TERMINAL_LOG.OUTCOME_APPROVED' },
    { value: 'declined', labelKey: 'TERMINAL_LOG.OUTCOME_DECLINED' },
    { value: 'error', labelKey: 'TERMINAL_LOG.OUTCOME_ERROR' },
    { value: 'cancelled', labelKey: 'TERMINAL_LOG.OUTCOME_CANCELLED' },
    { value: 'timeout', labelKey: 'TERMINAL_LOG.OUTCOME_TIMEOUT' }
  ];

  constructor(
    private ecommerceService: EcommerceService,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    if (this.storeId) {
      this.loadLogs();
    }
    this.countdown = this.refreshIntervalSec;
    this.startAutoRefresh();
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }

  startAutoRefresh(): void {
    this.stopAutoRefresh();
    this.countdown = this.refreshIntervalSec;

    this.refreshSubscription = interval(this.refreshIntervalSec * 1000).subscribe(() => {
      if (this.storeId && !this.showLogDetails) {
        this.loadLogsSilent();
      }
      this.countdown = this.refreshIntervalSec;
    });

    this.countdownSub = interval(1000).subscribe(() => {
      if (this.countdown > 0) {
        this.countdown--;
      }
    });
  }

  stopAutoRefresh(): void {
    this.refreshSubscription?.unsubscribe();
    this.countdownSub?.unsubscribe();
    this.refreshSubscription = undefined;
    this.countdownSub = undefined;
  }

  toggleAutoRefresh(): void {
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
  }

  onRefreshIntervalChange(): void {
    this.countdown = this.refreshIntervalSec;
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    }
  }

  private buildQueryParams(): any {
    const params: any = {
      page: this.currentPage,
      limit: this.pageSize
    };

    if (this.selectedOutcome) {
      params.outcome = this.selectedOutcome;
    }
    if (this.dateFrom) {
      params.dateFrom = this.dateFrom;
    }
    if (this.dateTo) {
      params.dateTo = this.dateTo;
    }
    if (this.searchText?.trim()) {
      params.search = this.searchText.trim();
    }
    if (this.orderSaveFailedOnly) {
      params.orderSaveFailed = 'true';
    }

    return params;
  }

  loadLogs(): void {
    const seq = ++this.logsLoadSeq;
    this.isLoading = true;
    this.errorMessage = '';

    this.ecommerceService.getTerminalLogs(this.storeId, this.buildQueryParams()).subscribe(
      (res: any) => {
        if (seq !== this.logsLoadSeq) return;
        this.applyLogsResponse(res);
        this.isLoading = false;
      },
      (error: any) => {
        if (seq !== this.logsLoadSeq) return;
        console.error('Failed to load terminal logs:', error);
        this.errorMessage = error?.error?.response?.message || 'TERMINAL_LOG.LOAD_ERROR';
        this.toastr.error(error?.error?.response?.message || 'Failed to load terminal logs');
        this.isLoading = false;
      }
    );
  }

  /** Silent reload — does not set isLoading so there is no spinner flash. */
  private loadLogsSilent(): void {
    const seq = ++this.logsLoadSeq;

    this.ecommerceService.getTerminalLogs(this.storeId, this.buildQueryParams()).subscribe(
      (res: any) => {
        if (seq !== this.logsLoadSeq) return;
        this.applyLogsResponse(res);
      },
      (error: any) => {
        if (seq !== this.logsLoadSeq) return;
        console.error('Silent terminal log refresh failed:', error);
      }
    );
  }

  private applyLogsResponse(res: any): void {
    const data = res?.response?.data ?? res?.data ?? null;
    if (data) {
      this.logs = data.logs || data.items || [];
      const pagination = data.pagination || {};
      this.totalItems = pagination.totalItems ?? pagination.total ?? 0;
      this.totalPages = pagination.totalPages ?? pagination.pages ?? 1;
      this.currentPage = pagination.currentPage ?? pagination.page ?? 1;
    } else {
      this.logs = [];
      console.warn('Unexpected terminal logs response:', res);
    }
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadLogs();
  }

  resetFilters(): void {
    this.dateFrom = '';
    this.dateTo = '';
    this.selectedOutcome = '';
    this.searchText = '';
    this.orderSaveFailedOnly = false;
    this.currentPage = 1;
    this.loadLogs();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadLogs();
  }

  viewLogDetails(log: any): void {
    this.stopAutoRefresh();
    this.selectedLog = log;
    this.showLogDetails = true;
    this.loadingDetail = true;
    this.resetReconcileForm();

    this.ecommerceService.getTerminalLogDetail(this.storeId, log._id).subscribe(
      (res: any) => {
        const data = res?.response?.data ?? res?.data ?? {};
        this.selectedLog = data.log || data;
        this.loadingDetail = false;
        if (this.canShowReconcilePanel()) {
          this.prepareReconcileForm();
        }
      },
      (error: any) => {
        this.loadingDetail = false;
        const msg = error?.error?.response?.message || 'Failed to load terminal log detail';
        this.toastr.error(msg);
      }
    );
  }

  closeLogDetails(): void {
    this.showLogDetails = false;
    this.selectedLog = null;
    this.resetReconcileForm();
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    }
  }

  resetReconcileForm(): void {
    this.reconcileMode = 'manual';
    this.reconciliationNote = '';
    this.orderSearchQuery = '';
    this.orderSearchResults = [];
    this.orderSearchLoading = false;
    this.selectedOrderId = '';
    this.manualItems = [];
    this.loadingBasketPrefill = false;
    this.basketPrefillSource = '';
    this.submittingReconcile = false;
  }

  hasOrderLink(log: any): boolean {
    if (!log) {
      return false;
    }
    return !!(log.orderId || log.order?._id || log.order);
  }

  isChargedNotRecorded(log: any): boolean {
    return log?.orderSaveFailed === true && !this.hasOrderLink(log);
  }

  isApprovedUnlinked(log: any): boolean {
    return log?.outcome === 'approved' && !this.hasOrderLink(log) && !log?.orderSaveFailed;
  }

  isReconciled(log: any): boolean {
    return this.hasOrderLink(log) && !!log?.reconciliation?.action;
  }

  canShowReconcilePanel(): boolean {
    return this.isChargedNotRecorded(this.selectedLog) || this.isApprovedUnlinked(this.selectedLog);
  }

  setReconcileMode(mode: 'link' | 'manual'): void {
    this.reconcileMode = mode;
    if (mode === 'manual' && this.manualItems.length === 0) {
      this.prepareManualItemsFromLog();
    }
  }

  prepareReconcileForm(): void {
    this.reconcileMode = 'manual';
    this.prepareManualItemsFromLog();
  }

  prepareManualItemsFromLog(): void {
    if (!this.selectedLog) {
      return;
    }

    this.loadingBasketPrefill = true;
    this.basketPrefillSource = '';

    const logDate = this.selectedLog.createdAt ? new Date(this.selectedLog.createdAt) : new Date();
    const startDate = new Date(logDate.getTime() - 24 * 60 * 60 * 1000).toISOString();
    const endDate = new Date(logDate.getTime() + 24 * 60 * 60 * 1000).toISOString();

    this.ecommerceService.getBasketAuditLogs(this.storeId, {
      startDate,
      endDate,
      limit: 200,
      page: 1
    }).subscribe(
      (res: any) => {
        const data = res?.response?.data ?? res?.data ?? {};
        const events = data.events || [];
        const cartId = this.selectedLog.cartId;
        const posSessionId = this.selectedLog.posSessionId;

        const matchingEvents = events.filter((event: any) =>
          (cartId && event.cartId === cartId) ||
          (posSessionId && event.posSessionId === posSessionId)
        );

        const items = this.extractItemsFromBasketEvents(matchingEvents);
        if (items.length > 0) {
          this.manualItems = items;
          this.basketPrefillSource = 'TERMINAL_LOG.RECONCILE_PREFILL_FROM_BASKET';
        } else {
          this.manualItems = [this.buildFallbackManualItem()];
          this.basketPrefillSource = 'TERMINAL_LOG.RECONCILE_PREFILL_FROM_AMOUNT';
        }
        this.loadingBasketPrefill = false;
      },
      () => {
        this.manualItems = [this.buildFallbackManualItem()];
        this.basketPrefillSource = 'TERMINAL_LOG.RECONCILE_PREFILL_FROM_AMOUNT';
        this.loadingBasketPrefill = false;
      }
    );
  }

  private extractItemsFromBasketEvents(events: any[]): any[] {
    const merged = new Map<string, any>();

    events.forEach((event: any) => {
      (event.items || []).forEach((item: any) => {
        const key = `${item.productId || item.productName || 'item'}-${item.unitPrice || 0}`;
        const existing = merged.get(key);
        if (existing) {
          existing.quantity += item.quantity || 1;
        } else {
          merged.set(key, {
            productName: item.productName || 'Unknown product',
            productSku: item.productSku || item.sku || '',
            productId: item.productId || null,
            quantity: item.quantity || 1,
            unitPrice: item.unitPrice || 0
          });
        }
      });
    });

    return Array.from(merged.values());
  }

  private buildFallbackManualItem(): any {
    const amount = this.selectedLog?.amount ?? 0;
    return {
      productName: 'Manual reconciliation item',
      productSku: '',
      productId: null,
      quantity: 1,
      unitPrice: amount
    };
  }

  addManualItemRow(): void {
    this.manualItems.push({
      productName: '',
      productSku: '',
      productId: null,
      quantity: 1,
      unitPrice: 0
    });
  }

  removeManualItemRow(index: number): void {
    if (this.manualItems.length <= 1) {
      this.toastr.warning('At least one line item is required');
      return;
    }
    this.manualItems.splice(index, 1);
  }

  getManualItemsTotal(): number {
    return this.manualItems.reduce(
      (sum, item) => sum + ((item.quantity || 0) * (item.unitPrice || 0)),
      0
    );
  }

  manualItemsTotalMismatch(): boolean {
    if (this.selectedLog?.amount == null) {
      return false;
    }
    return Math.abs(this.getManualItemsTotal() - this.selectedLog.amount) > 0.01;
  }

  searchOrdersForReconcile(): void {
    const query = this.orderSearchQuery?.trim();
    if (!query) {
      this.toastr.warning('Enter an order number or customer identifier to search');
      return;
    }

    this.orderSearchLoading = true;
    this.orderSearchResults = [];
    this.selectedOrderId = '';

    this.ecommerceService.searchOrders(this.storeId, {
      q: query,
      limit: 15,
      page: 1,
      sortBy: 'orderDate',
      sortOrder: 'desc'
    }).subscribe(
      (res: any) => {
        const data = res?.response?.data ?? res?.data ?? {};
        this.orderSearchResults = data.orders || [];
        this.orderSearchLoading = false;
        if (this.orderSearchResults.length === 0) {
          this.toastr.info('No matching orders found');
        }
      },
      (error: any) => {
        this.orderSearchLoading = false;
        this.toastr.error(error?.error?.response?.message || 'Order search failed');
      }
    );
  }

  selectOrderForReconcile(order: any): void {
    this.selectedOrderId = order._id;
  }

  formatOrderCustomer(order: any): string {
    const user = order?.userId;
    if (user && typeof user === 'object') {
      const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
      if (name) {
        return name;
      }
      if (user.email) {
        return user.email;
      }
    }
    return order?.customer?.name || 'Guest / walk-in';
  }

  formatOrderAmount(order: any): string {
    const amount = order?.finalAmount ?? order?.totalAmount;
    if (amount == null) {
      return '—';
    }
    const currency = order?.currency || this.selectedLog?.currency || 'DKK';
    try {
      return new Intl.NumberFormat('da-DK', { style: 'currency', currency }).format(amount);
    } catch {
      return `${amount} ${currency}`;
    }
  }

  orderAmountMismatch(order: any): boolean {
    if (!order || this.selectedLog?.amount == null) {
      return false;
    }
    const amount = order.finalAmount ?? order.totalAmount;
    return amount != null && Math.abs(amount - this.selectedLog.amount) > 0.01;
  }

  isReconcileSubmitDisabled(): boolean {
    if (this.submittingReconcile || !this.reconciliationNote?.trim()) {
      return true;
    }
    if (this.reconcileMode === 'link') {
      return !this.selectedOrderId;
    }
    return this.manualItems.length === 0 ||
      this.manualItems.some(item => !item.productName?.trim() || !item.quantity || item.unitPrice == null);
  }

  submitReconcile(): void {
    if (this.isReconcileSubmitDisabled() || !this.selectedLog?._id) {
      if (!this.reconciliationNote?.trim()) {
        this.toastr.error('A reconciliation note is required');
      }
      return;
    }

    const actionLabel = this.reconcileMode === 'link'
      ? 'link this payment to the selected order'
      : 'create a manual reconciliation order';

    Swal.fire({
      title: 'Confirm reconciliation',
      html: `This will permanently ${actionLabel}.<br><br><strong>Note:</strong> ${this.escapeHtml(this.reconciliationNote.trim())}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Reconcile',
      confirmButtonColor: '#6f42c1'
    }).then(result => {
      if (result.isConfirmed) {
        this.performReconcile();
      }
    });
  }

  private performReconcile(): void {
    const payload: any = {
      action: this.reconcileMode === 'link' ? 'link_existing_order' : 'create_manual_order',
      note: this.reconciliationNote.trim()
    };

    if (this.reconcileMode === 'link') {
      payload.orderId = this.selectedOrderId;
    } else {
      payload.items = this.manualItems.map(item => ({
        name: item.productName.trim(),
        quantity: Number(item.quantity) || 1,
        unitPrice: Number(item.unitPrice) || 0,
        sku: item.productSku?.trim() || null,
        productId: item.productId || null
      }));
    }

    this.submittingReconcile = true;

    this.ecommerceService.reconcileTerminalLog(this.selectedLog._id, payload).subscribe(
      (res: any) => {
        const data = res?.response?.data ?? res?.data ?? {};
        const order = data.order;
        const linkedOrder = order
          ? {
              _id: order._id,
              orderNumber: order.orderNumber,
              status: order.status
            }
          : null;

        this.selectedLog = {
          ...this.selectedLog,
          orderId: data.orderId || order?._id || this.selectedOrderId,
          order: linkedOrder,
          reconciliation: {
            action: payload.action,
            note: payload.note,
            reconciledAt: new Date().toISOString(),
            amountMismatchWarning: data.amountMismatchWarning || null
          }
        };

        this.updateLogInList(this.selectedLog);
        this.submittingReconcile = false;
        this.toastr.success(res?.response?.message || 'Terminal log reconciled successfully');

        if (data.amountMismatchWarning) {
          Swal.fire({
            title: 'Amount mismatch warning',
            text: data.amountMismatchWarning,
            icon: 'warning'
          });
        }
      },
      (error: any) => {
        this.submittingReconcile = false;
        const msg = error?.error?.response?.message || error?.error?.error || 'Reconciliation failed';
        this.toastr.error(msg);
      }
    );
  }

  private updateLogInList(updatedLog: any): void {
    const index = this.logs.findIndex(log => log._id === updatedLog._id);
    if (index >= 0) {
      this.logs[index] = { ...this.logs[index], ...updatedLog };
    }
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  navigateToLinkedOrder(orderRef: any): void {
    if (!orderRef) return;
    const orderNumber = typeof orderRef === 'object' ? orderRef.orderNumber : null;
    if (!orderNumber) return;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: 'orders', orderNumber },
      queryParamsHandling: 'merge'
    });
  }

  getOutcomeBadgeClass(outcome: string): string {
    switch (outcome) {
      case 'approved': return 'bg-success';
      case 'declined': return 'bg-danger';
      case 'error': return 'bg-danger';
      case 'cancelled': return 'bg-secondary';
      case 'timeout': return 'bg-warning text-dark';
      default: return 'bg-secondary';
    }
  }

  getOutcomeLabelKey(outcome: string): string {
    switch (outcome) {
      case 'approved': return 'TERMINAL_LOG.OUTCOME_APPROVED';
      case 'declined': return 'TERMINAL_LOG.OUTCOME_DECLINED';
      case 'error': return 'TERMINAL_LOG.OUTCOME_ERROR';
      case 'cancelled': return 'TERMINAL_LOG.OUTCOME_CANCELLED';
      case 'timeout': return 'TERMINAL_LOG.OUTCOME_TIMEOUT';
      default: return 'TERMINAL_LOG.OUTCOME_UNKNOWN';
    }
  }

  formatDeclineReason(log: any): string {
    if (!log?.declineReason && !log?.declineReasonFriendly) {
      return '—';
    }
    let text = log.declineReason || log.declineReasonFriendly || '';
    if (log.declineReasonFriendly && log.declineReason && log.declineReason !== log.declineReasonFriendly) {
      text = `${log.declineReasonFriendly} (${log.declineReason})`;
    } else if (log.declineReasonFriendly) {
      text = log.declineReasonFriendly;
    }
    if (log.vivaEventId) {
      text += ` (Event ID ${log.vivaEventId})`;
    }
    return text;
  }

  formatTerminal(log: any): string {
    if (log?.terminalName) {
      return log.terminalName;
    }
    return log?.terminalId || '—';
  }

  formatAmount(log: any): string {
    if (log?.amount == null && log?.amount !== 0) {
      return '—';
    }
    const currency = log?.currency || 'DKK';
    try {
      return new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency
      }).format(log.amount);
    } catch {
      return `${log.amount} ${currency}`;
    }
  }

  formatCard(log: any): string {
    const parts: string[] = [];
    if (log?.maskedPan) {
      parts.push(log.maskedPan);
    }
    if (log?.cardType) {
      parts.push(log.cardType);
    }
    return parts.length ? parts.join(' · ') : '—';
  }

  prettyJson(value: any): string {
    if (value == null) {
      return '—';
    }
    if (typeof value === 'string') {
      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch {
        return value;
      }
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  getLinkedOrder(log: any): any {
    if (log?.order) {
      return log.order;
    }
    if (log?.orderId && typeof log.orderId === 'object') {
      return {
        _id: log.orderId._id,
        orderNumber: log.orderId.orderNumber,
        status: log.orderId.status
      };
    }
    return null;
  }

  trackByLogId(index: number, log: any): string {
    return log._id || String(index);
  }
}
